import React, { useState, useEffect } from 'react';
import Loader from '../Loader';
import { useTranslation } from 'react-i18next';
import FilterButton from '../../components/Admin/Investigations/FilterButton';
import InvestigationsTable from '../../components/Admin/Investigations/InvestigationsTable';
import InvestigationDetail from '../../components/Admin/Investigations/InvestigationDetail';
import {
  getInvestigations,
  InvestigationStatus,
  InvestigationList,
  InvestigationItem,
} from '../../api/investigationsAdminService';

interface Props {
  status: InvestigationStatus;
  onSelectStatus: (s: InvestigationStatus) => void;
}

const PAGE_SIZE = 50;
const STATUSES: InvestigationStatus[] = [
  'accepted',
  'under_review',
  'rejected',
  'deleted_by_user',
];

const InvestigationsView: React.FC<Props> = ({ status, onSelectStatus }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [data, setData] = useState<InvestigationList | null>(null);
  const [selected, setSelected] = useState<InvestigationItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getInvestigations(status, page, PAGE_SIZE)
      .then((l) => {
        setData(l);
        // si la selección ya no existe, la limpiamos
        if (selected) {
          const exists = l.items.find((it) => it.id === selected.id);
          setSelected(exists || null);
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [status, page]);

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  if (loading) return <Loader />;
  if (error)   return <p className="text-red-500">{error}</p>;

  return (
    <div className="space-y-6">
      {/* filtros */}
      <div className="flex gap-2 flex-wrap">
        {STATUSES.map((s) => (
          <FilterButton
            key={s}
            active={s === status}
            onClick={() => {
              onSelectStatus(s);
              setPage(0);
            }}
          >
            {t(`admin.status.${s}`)}
          </FilterButton>
        ))}
      </div>

      {/* grid 2 columnas */}
      <div className="grid lg:grid-cols-2 gap-4">
        <InvestigationsTable
          items={data?.items ?? []}
          selectedId={selected?.id ?? null}
          onSelect={setSelected}
        />

        <InvestigationDetail
          investigation={selected}
          refresh={load}
        />
      </div>

      {/* paginación */}
      <div className="flex items-center justify-center gap-4">
        <FilterButton
          onClick={() => setPage((p) => Math.max(p - 1, 0))}
          disabled={page === 0}
        >
          « {t('admin.prev')}
        </FilterButton>
        <span>{page + 1} / {totalPages}</span>
        <FilterButton
          onClick={() => setPage((p) => p + 1)}
          disabled={page + 1 >= totalPages}
        >
          {t('admin.next')} »
        </FilterButton>
      </div>
    </div>
  );
};

export default InvestigationsView;
